import Typography from '@/components/Typography';
import { TextStyles } from '@/constants';
import Link from 'next/link';
import styled from 'styled-components';

// Container only gains its horizontal padding from `md` up, so below that the page
// has to supply its own — without it the cards sit flush against the screen edge.
export const BlogWrapper = styled.div(({ theme }) => ({
  padding: `${theme.spacing[32]} ${theme.spacing[16]} ${theme.spacing[48]}`,

  [`${theme.mediaBreakpointUp.md}`]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export const BlogTitle = styled(Typography).attrs({
  variant: TextStyles['Heading 1'],
  type: 'h1',
})(({ theme }) => ({
  color: theme.colors.highEmphasisBlack,
  marginBottom: theme.spacing[8],
}));

export const BlogDescription = styled(Typography).attrs({
  variant: TextStyles['Body Text Large'],
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
  marginBottom: theme.spacing[24],
}));

export const TagFilterList = styled.ul(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  margin: `0 0 ${theme.spacing[24]} 0`,
  padding: 0,
  listStyle: 'none',
}));

export const TagChip = styled(Link).withConfig({
  shouldForwardProp: (propName) => propName.toString() !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: `${theme.spacing[6]} ${theme.spacing[16]}`,
  borderRadius: theme.spacing[24],
  border: `1px solid ${isActive ? theme.colors.blue : theme.colors.gray01}`,
  backgroundColor: isActive ? theme.colors.blue : theme.colors.highEmphasisWhite,
  color: isActive ? theme.colors.highEmphasisWhite : theme.colors.highEmphasisBlack,
  textDecoration: 'none',

  '&:hover': {
    borderColor: theme.colors.blue,
  },
}));

export const TagChipText = styled(Typography).attrs({
  variant: TextStyles['Heading 6'],
  type: 'span',
})({
  color: 'inherit',
});

export const BlogGrid = styled.ul<{ isPending?: boolean }>(({ theme, isPending }) => ({
  display: 'grid',
  opacity: isPending ? 0.5 : 1,
  pointerEvents: isPending ? 'none' : undefined,
  transition: 'opacity 150ms ease-out',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gap: theme.spacing[24],
  margin: 0,
  padding: 0,
  listStyle: 'none',

  [`${theme.mediaBreakpointUp.md}`]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [`${theme.mediaBreakpointUp.lg}`]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

export const BlogCardLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: theme.spacing[8],
  border: `1px solid ${theme.colors.gray01}`,
  backgroundColor: theme.colors.highEmphasisWhite,
  overflow: 'hidden',
  textDecoration: 'none',

  '&:hover': {
    boxShadow: theme.elevation[3],
  },
}));

export const BlogCardThumbnail = styled.span(({ theme }) => ({
  display: 'block',
  // `position: relative` + a fixed ratio is what next/image's `fill` layout anchors to;
  // the ratio also reserves the space before the image loads, so the grid never shifts.
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  backgroundColor: theme.colors.surface02,

  img: {
    objectFit: 'cover',
  },
}));

export const BlogCardBody = styled.span(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  padding: theme.spacing[16],
}));

export const BlogCardTitle = styled(Typography).attrs({
  variant: TextStyles['Heading 3'],
  type: 'span',
})(({ theme }) => ({
  color: theme.colors.highEmphasisBlack,
}));

export const BlogCardExcerpt = styled(Typography).attrs({
  variant: TextStyles['Body Text Small'],
  type: 'span',
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
}));

export const BlogCardDate = styled(Typography).attrs({
  variant: TextStyles['Caption Text'],
  type: 'span',
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
}));

export const NoPostsText = styled(Typography).attrs({
  variant: TextStyles['Body Text Large'],
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
  padding: `${theme.spacing[32]} 0`,
  textAlign: 'center',
}));

// Article page

export const ArticleWrapper = styled.article(({ theme }) => ({
  maxWidth: '760px',
  margin: '0 auto',
  padding: `${theme.spacing[32]} ${theme.spacing[16]} ${theme.spacing[48]}`,

  [`${theme.mediaBreakpointUp.md}`]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export const ArticleTitle = styled(Typography).attrs({
  variant: TextStyles['Large Text Display'],
  type: 'h1',
})(({ theme }) => ({
  color: theme.colors.highEmphasisBlack,
  marginBottom: theme.spacing[12],
}));

// Tags sit above the H1 on an article, so the gap to the title is tighter than the
// listing page's gap to the grid.
export const ArticleTagList = styled(TagFilterList)(({ theme }) => ({
  marginBottom: theme.spacing[12],
}));

export const ArticleMeta = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[12],
  marginBottom: theme.spacing[24],
}));

export const ArticleDate = styled(Typography).attrs({
  variant: TextStyles['Body Text Small'],
  type: 'time',
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
}));

export const ArticleContent = styled.div(({ theme }) => ({
  marginBottom: theme.spacing[32],
}));

export const RelatedArticlesSection = styled.section(({ theme }) => ({
  marginTop: theme.spacing[32],
  paddingTop: theme.spacing[32],
  borderTop: `1px solid ${theme.colors.gray01}`,
}));

export const RelatedArticlesTitle = styled(Typography).attrs({
  variant: TextStyles['Heading 2'],
  type: 'h2',
})(({ theme }) => ({
  color: theme.colors.highEmphasisBlack,
  marginBottom: theme.spacing[16],
}));
